import { useRef, type ComponentProps } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { FlightSortOptions } from 'constants/flight'
import useFlightSortOptions from 'hooks/Flights/useFlightSortOptions'
import { getOtherSortOptions } from 'lib/flights/flightUtils'
import ToggleOptionsGroup from 'LuxKit/ToggleOptionsGroup'
import ToggleOption from 'LuxKit/ToggleOption'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidDirectionIcon from 'LuxKit/Icons/solid/SolidDirectionIcon'
import LineRowDoubleIcon from 'LuxKit/Icons/line/LineRowDoubleIcon'
import LineRowSingleIcon from 'LuxKit/Icons/line/LineRowSingleIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import RadioButton from 'LuxKit/Radio/RadioButton'
import RichTooltip from 'LuxKit/RichTooltip'
import useToggle from 'hooks/useToggle'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const FlightSortingToggleOption = styled(ToggleOption)`
  border-bottom: ${rem(3)} solid transparent;
  border-radius: 0;
  &&& {
    box-shadow: none;
  }

  &:not(:first-child) {
    border-inline-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  && + && {
    margin-inline-start: 0;
  }

  &:first-child {
    border-end-start-radius: ${props => props.theme.borderRadius.M};
    border-start-start-radius: ${props => props.theme.borderRadius.M};
  }

  &:last-child {
    border-end-end-radius: ${props => props.theme.borderRadius.M};
    border-start-end-radius: ${props => props.theme.borderRadius.M};
  }

  &.is-selected {
    border-bottom-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    &:not(.is-loading) {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

      body.${REBRAND_PALETTE_CLASS} & {
        background-color: var(--palette-neutral-surface-light-background-alt);
      }
    }
  }
`

const ViewToggleOption = styled(FlightSortingToggleOption)`
  &&&& {
    width: 100%;
    min-width: 0;
  }
`

const ViewToggleTooltip = styled(RichTooltip)`
  &&&& {
    display: flex;
    flex: 0 0 ${rem(80)};
    min-width: 0;
  }

  border-inline-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
`
const FlightSortingBarContainer = styled.div`
  overflow: hidden;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CheapestPrice = styled.span`
  color: ${props => props.theme.palette.messaging.success.darkForeground};
`

interface SortOptionSubtitleProps {
  price: number
  hours: number
  minutes: number
  isAverageDuration: boolean
  highlightCheapest?: boolean
}

function SortOptionSubtitle(props: SortOptionSubtitleProps) {
  const {
    price,
    hours,
    minutes,
    isAverageDuration,
    highlightCheapest,
  } = props

  const formattedPrice = highlightCheapest ? (
    <CheapestPrice>
      <FormatCurrency value={price} format="roundedDollar" />
    </CheapestPrice>
  ) : (
    <FormatCurrency value={price} format="roundedDollar" />
  )

  if (isAverageDuration) {
    return (
      <FormattedMessage
        id="_nQwOnutXTUHkCop96b1"
        defaultMessage="{price} · {hours}h {minutes}m (Avg)"
        description="Flight sorting option subtitle showing price and average outbound and return duration"
        values={{
          price: formattedPrice,
          hours,
          minutes,
        }}
      />
    )
  }

  return (
    <FormattedMessage
      id="fRphjVfNe-zHRtidBDEQ"
      defaultMessage="{price} · {hours}h {minutes}m"
      description="Flight sorting option subtitle showing the price and total duration for each flight"
      values={{
        price: formattedPrice,
        hours,
        minutes,
      }}
    />
  )
}

interface Props {
  sort?: FlightSortOptions
  destinationAirportCode: string
  originAirportCode: string
  onChange?: (newSort: FlightSortOptions) => void
  optionValues: Record<FlightSortOptions, { time: { hours: number, minutes: number }, price: number }>
  loading?: boolean
  showViewToggle?: boolean
  isCombinedTileView?: boolean
  onViewToggle?: () => void
  dropdownBoundaryRef?: ComponentProps<typeof DropdownList>['boundaryRef']
  dropdownPortaled?: boolean
  dropdownAutoHeight?: boolean
}

function FlightSortingBar(props: Props) {
  const {
    sort = FlightSortOptions.Best,
    onChange = noop,
    originAirportCode,
    destinationAirportCode,
    optionValues,
    loading,
    showViewToggle = false,
    isCombinedTileView = false,
    onViewToggle = noop,
    dropdownBoundaryRef,
    dropdownPortaled,
    dropdownAutoHeight,
  } = props
  const intl = useIntl()
  const sortOptions = useFlightSortOptions()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, toggleDropDown, _, closeDropdown] = useToggle()

  const otherSortOptions = getOtherSortOptions(
    originAirportCode,
    destinationAirportCode,
  )

  const showSeparateLegsTooltip = intl.formatMessage({
    id: 'M7ykEZvN_yNbbOYHlRAd',
    defaultMessage: 'Show each leg separately',
    description: 'Tooltip on the View toggle when switching from combined to single-leg flight tiles',
  })
  const showCombinedLegsTooltip = intl.formatMessage({
    id: 'WFfZPOr-UYhKSAgDxTuZ',
    defaultMessage: 'Show each leg combined',
    description: 'Tooltip on the View toggle when switching from single-leg to combined flight tiles',
  })
  const viewToggleTooltip = isCombinedTileView ? showSeparateLegsTooltip : showCombinedLegsTooltip

  const handleClick = (
    evt: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const key = evt.currentTarget.dataset.key
    if (key) {
      onChange(key as FlightSortOptions)
      closeDropdown()
    }
  }

  const otherSortingSelected = otherSortOptions.find((s) => s.value === sort)

  const otherSortingTitle = otherSortingSelected ? otherSortingSelected.label : intl.formatMessage({
    id: 'tdHD1_JGpTHz91egUlwa',
    defaultMessage: 'Other sorting',
    description: 'Fallback title for the other sorting options toggle in flight sorting bar',
  })

  const otherSubtitle = otherSortingSelected ? (
    <SortOptionSubtitle
      price={optionValues[sort].price}
      hours={optionValues[sort].time.hours}
      minutes={optionValues[sort].time.minutes}
      isAverageDuration={isCombinedTileView}
    />
  ) : null

  return (
    <>
      <FlightSortingBarContainer>
        <ToggleOptionsGroup horizontalAlign="stretch">
          {sortOptions.map((option) => (
            <FlightSortingToggleOption
              key={option.value}
              className={loading ? 'is-loading' : undefined}
              data-key={option.value}
              title={option.label}
              subtitle={
                <>
                  {loading && (
                    <BodyText variant="small">
                      <TextLoadingBox width="12ch" />
                    </BodyText>
                  )}

                  {!loading && (
                    <SortOptionSubtitle
                      price={optionValues[option.value].price}
                      hours={optionValues[option.value].time.hours}
                      minutes={optionValues[option.value].time.minutes}
                      isAverageDuration={isCombinedTileView}
                      highlightCheapest={option.value === FlightSortOptions.Cheapest}
                    />
                  )}
                </>
              }
              onClick={handleClick}
              isSelected={option.value === sort}
              disabled={loading}
            />
          ))}

          <FlightSortingToggleOption
            ref={triggerRef}
            className={loading ? 'is-loading' : undefined}
            endIcon={<SolidDirectionIcon size="XS" />}
            title={otherSortingTitle}
            titleWrap="word-wrap"
            subtitle={otherSubtitle}
            onClick={toggleDropDown}
            isSelected={Boolean(otherSortingSelected)}
            disabled={loading}
          />

          {showViewToggle && <ViewToggleTooltip
            portaled
            dismissible={false}
            placement="bottom"
            variant="dark"
            showOnHover
            description={viewToggleTooltip}
          >
            <ViewToggleOption
              endIcon={isCombinedTileView ? <LineRowSingleIcon size="XS" /> : <LineRowDoubleIcon size="XS" />}
              title={intl.formatMessage({
                id: 'EHgNEygd7SqIMt_q-MEd',
                defaultMessage: 'View',
                description: 'Button label that switches between combined and single-leg flight tile layouts',
              })}
              onClick={onViewToggle}
            />
          </ViewToggleTooltip>}
        </ToggleOptionsGroup>
      </FlightSortingBarContainer>

      <DropdownList
        size="S"
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        open={isDropdownOpen}
        onClose={closeDropdown}
        boundaryRef={dropdownBoundaryRef}
        portaled={dropdownPortaled}
        autoHeight={dropdownAutoHeight}
        crossAxisShift={dropdownPortaled}
      >
        {otherSortOptions.map((option) => (
          <ClickableListItem
            key={option.value}
            data-key={option.value}
            onClick={handleClick}
            startIcon={<RadioButton checked={option.value === sort} />}
            subtitle={
              <SortOptionSubtitle
                price={optionValues[option.value].price}
                hours={optionValues[option.value].time.hours}
                minutes={optionValues[option.value].time.minutes}
                isAverageDuration={isCombinedTileView}
              />
            }
            selected={option.value === sort}
            title={option.label}
          />
        ))}
      </DropdownList>
    </>
  )
}

export default FlightSortingBar
